import { MEDIA } from "@/lib/media";

export const NAV = [
  { to: "/approach", label: "Our Approach" },
  { to: "/courses", label: "Courses" },
  { to: "/on-country", label: "On-Country" },
  { to: "/seed-banks", label: "Seed Banks" },
  { to: "/organisations", label: "Organisations" },
  { to: "/library", label: "Resources" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const HHC = [
  {
    key: "Head",
    label: "Understanding",
    line: "Understand the plant, the seed and the ecological system.",
    body: "Ecological literacy and an understanding of plant biology, native seed systems, restoration ecology, seasonal cycles, provenance, dormancy, viability and regeneration.",
    image: MEDIA.floraWhite,
  },
  {
    key: "Hands",
    label: "Practice",
    line: "Develop practical skill through field work, processing, storage and propagation.",
    body: "Field identification, seed collection, handling, cleaning, processing, testing, storage, propagation, nursery work and restoration planning.",
    image: MEDIA.handsSeed,
  },
  {
    key: "Country",
    label: "Relationship",
    line: "Learn from place, season, local knowledge and the relationships that sustain regeneration.",
    body: "Read landscapes, recognise ecological relationships, respect cultural authority, understand local priorities and work with Country rather than treating it as a passive project site.",
    image: MEDIA.landscape,
  },
];

export const WHY_CHAPTERS = [
  {
    n: "01",
    title: "Seed is ecological infrastructure",
    body: "Native seed is not simply a material used at the end of restoration projects. It is living infrastructure — the material that connects past landscapes with future recovery.",
  },
  {
    n: "02",
    title: "Recovery can no longer be assumed",
    body: "After the Black Summer fires it became clear that natural regeneration cannot always be relied upon. Where fire burns hot enough to damage living plants and soil seed banks, recovery may require deliberate collection, storage, propagation and reintroduction of appropriate species.",
  },
  {
    n: "03",
    title: "Rehabilitation is a decades-long relationship",
    body: "Mine closure cannot be reduced to stabilising landforms or meeting short-term compliance. It means supporting the return of functioning landscapes over decades, and in some cases generations.",
  },
  {
    n: "04",
    title: "The Academy equips people to take part",
    body: "SeedKeepers Academy develops the ecological understanding, practical skill and relationship with Country that ecological recovery depends upon.",
  },
];

export const WHY_POINTS = [
  "Post-fire ecological recovery",
  "Mine closure and rehabilitation",
  "Biodiversity and habitat restoration",
  "Community and regional seed security",
  "Long-term resilience of Country",
];

export const LEARN_SKILLS = [
  "Identify priority native species and assess seed maturity",
  "Plan and undertake ethical seed collection",
  "Clean, test and store native seed for future use",
  "Develop seed collection calendars and field protocols",
  "Establish or improve community and organisational seed banks",
  "Support propagation and nursery production",
  "Contribute to post-fire recovery, mine rehabilitation and ecological restoration",
  "Develop native seed enterprises and regional supply networks",
];

export const PATHWAYS_LEARNING = [
  { title: "Online Foundation Courses", body: "Flexible introductory and specialist learning that provides the scientific and practical foundations for native seed work.", to: "/courses" },
  { title: "On-Country Native Seed Intensives", body: "Hands-on field and classroom learning covering identification, collection, cleaning, storage, propagation fundamentals and field planning.", to: "/on-country" },
  { title: "Seed Bank Development", body: "Support for organisations to understand what kind of seed bank they need, what it should support on-country, and how collections, facilities, governance and skills develop together.", to: "/seed-banks" },
  { title: "Mentoring & Project Support", body: "Follow-up support to help participants apply learning to real collection, propagation, nursery, restoration or enterprise projects.", to: "/contact" },
  { title: "Regional & Custom Programs", body: "Programs designed around local species, seasons, organisational needs and regional restoration priorities.", to: "/organisations" },
];

export const INQUIRY_EXAMPLES = [
  "Prepare a seasonal seed collection plan",
  "Document the seed cycle of priority local species",
  "Process and store a trial collection",
  "Develop an initial seed bank plan",
  "Establish a germination trial",
  "Map seed needs for a restoration project",
  "Create a field reference guide for a ranger team",
];

export const WHO_FOR = [
  "Beginning native seed work",
  "Already collecting seed and want stronger systems",
  "Managing ranger, nursery or restoration teams",
  "Planning a seed bank",
  "Preparing for restoration or fire recovery",
  "Working in mine closure or land rehabilitation",
  "Developing a native seed enterprise",
  "Wanting the ecology behind the practical work",
];

export const COURSE_CATALOGUE = [
  { title: "Native Seed Foundations", format: "Online · Self-paced", level: "Introductory", duration: "~4 weeks", purpose: "Plant structure, flowering, fruiting, seed formation and maturity, ecological context and terminology.", audience: "Anyone new to native seed work", skills: ["Plant biology", "Seed formation", "Ecological literacy"] },
  { title: "Identification & Field Observation", format: "Online + optional field", level: "Introductory", duration: "~4 weeks", purpose: "Plant identification, reproductive material, phenology, field notes, seasonal monitoring and priority species.", audience: "Collectors and ranger teams", skills: ["Identification", "Phenology", "Field notes"] },
  { title: "Ethical Seed Collection", format: "Online + field", level: "Core", duration: "~6 weeks", purpose: "Collection planning, permissions, cultural authority, provenance, limits, hygiene, safety, methods and documentation.", audience: "Practitioners and enterprises", skills: ["Provenance", "Permissions", "Field method"] },
  { title: "Seed Cleaning & Processing", format: "On-country + online", level: "Core", duration: "~6 weeks", purpose: "Drying, extraction, cleaning, screening, contamination management, weighing, labelling and batch records.", audience: "Seed bank and nursery teams", skills: ["Cleaning", "Batch records", "Quality"] },
  { title: "Seed Storage & Seed Banks", format: "Online + advisory", level: "Core", duration: "~6 weeks", purpose: "Seed behaviour, moisture, temperature, containers, pests, viability, monitoring, management and seed bank design.", audience: "Organisations & land managers", skills: ["Storage", "Viability", "Bank design"] },
  { title: "Germination & Propagation", format: "On-country + online", level: "Core", duration: "~8 weeks", purpose: "Dormancy, treatments, germination trials, propagation media, irrigation, nutrition, plant health and hardening.", audience: "Nursery and restoration teams", skills: ["Dormancy", "Propagation", "Trials"] },
  { title: "Restoration & Field Planning", format: "Online + field", level: "Advanced", duration: "~8 weeks", purpose: "Seed demand, target communities, reference ecosystems, seasonal plans, logistics, direct seeding and monitoring.", audience: "Restoration practitioners", skills: ["Planning", "Direct seeding", "Monitoring"] },
  { title: "Native Seed Enterprise", format: "Online + mentoring", level: "Advanced", duration: "~10 weeks", purpose: "Costing and pricing, quality assurance, provenance and traceability, record keeping, ethical commercialisation, contracts and procurement, seed supply chains, market development, business planning, enterprise governance, workforce development and regional opportunities.", audience: "Emerging enterprises", skills: ["Provenance & traceability", "Quality assurance", "Seed supply chain management", "Business planning", "Workforce development", "Indigenous enterprise models (where appropriate)", "Investment readiness", "Customer relationships & long-term supply agreements"] },
  { title: "GIS, Mapping & Digital Field Systems", format: "Online + field", level: "Specialist", duration: "~6 weeks", purpose: "Use mapping and digital tools to plan, manage and monitor native seed programs. Learn GIS fundamentals, species mapping, collection planning, field data capture, provenance mapping, restoration planning and how digital platforms such as the SPIRIT App support seed collection, seed banking and long-term landscape management. Includes access to the SPIRIT App.", audience: "Practitioners, rangers & organisations", skills: ["GIS fundamentals", "Species mapping", "Collection planning", "Field data capture", "Provenance mapping", "Restoration planning", "SPIRIT App"], link: { label: "Explore the SPIRIT App", href: "https://www.seedkeepers.app" } },
];

export const ONCOUNTRY_STEPS = ["Identify", "Plan", "Collect", "Document", "Handle", "Clean", "Store", "Propagate", "Apply"];

export const ONCOUNTRY_TAKEAWAYS = [
  "Identify suitable target species",
  "Recognise seed maturity",
  "Select appropriate collection methods",
  "Complete field and provenance records",
  "Handle material after collection",
  "Clean a range of seed types",
  "Prepare storage batches",
  "Select storage conditions",
  "Plan germination trials",
  "Prepare future collection calendars",
  "Connect collection effort to restoration needs",
];

export const SEEDBANK_STEPS = [
  { n: "01", title: "Purpose & Country", body: "Clarify what landscapes, communities, cultural priorities and restoration needs the seed bank will support." },
  { n: "02", title: "Seed & Collection Landscape", body: "Identify priority species, seasonal timing, likely collection zones, provenance requirements and existing knowledge." },
  { n: "03", title: "People & Governance", body: "Determine who holds authority, who manages collections, who collects, who accesses seed and how decisions are recorded." },
  { n: "04", title: "Processing & Storage", body: "Assess cleaning requirements, equipment, containers, environmental controls, pest risks, viability monitoring and workflow." },
  { n: "05", title: "Propagation & Use", body: "Connect collections to nursery propagation, direct seeding, fire recovery, restoration, conservation and possible enterprise." },
  { n: "06", title: "Facilities & Implementation", body: "Develop a realistic staged plan for facilities, equipment, training, operating procedures, budgets and growth." },
];

export const ORG_NEEDS = [
  "Team training", "Tailored curriculum", "Seed bank planning", "Restoration readiness",
  "Fire recovery capability", "Mine closure & rehabilitation", "Ranger development", "Native seed enterprise support",
];
